import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { Loading } from "shared-ui/global/Loading";
import { loginWithAxleAppSwitcher } from "shared-ui/utils/tokens";
import { useCookieContext } from "../../contexts/Cookie";
import { SsoError, SsoInactiveOrg, SsoUserNotFound } from "./LoginPage";
import { useEnvironmentType } from "../../contexts/useEnvType";

export const LoginWithAxlePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showGenericError, setShowGenericError] = useState<boolean>(false);
  const [showJwtExpiredError, setShowJwtExpiredError] = useState<boolean>(false);
  const [showNoRolesError, setShowNoRolesError] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<SsoError | null>(null);
  const [token] = useState(() => new URLSearchParams(window.location.search).get("token"));
  const { forceRefresh } = useCookieContext();
  const { isGov } = useEnvironmentType();

  useEffect(() => {
    const tokenFromUrl = token;
    if (tokenFromUrl) {
      const params = new URLSearchParams(window.location.search);
      params.delete("token");

      loginWithAxleAppSwitcher(tokenFromUrl)
        .then(() => {
          forceRefresh();
          navigate("/");
        })
        .catch(err => {
          const errors = err?.response?.data?.errors ?? [err.message];
          errors.forEach((eString: string) => {
            if (eString === "Org Membership not found") {
              setLoginError(SsoUserNotFound);
            } else if (eString === "OrgMembership inactive") {
              setLoginError(SsoInactiveOrg);
            } else if (eString === "ERR_JWT_EXPIRED") {
              setShowJwtExpiredError(true);
            } else if (eString === "OrgMembership has no roles assigned") {
              setShowNoRolesError(true);
            } else {
              setShowGenericError(true);
            }
            setLoading(false);
          });
        });
    } else {
      setLoading(false);
      setShowGenericError(true);
    }
  }, []);

  const supportEmail = isGov ? "tacsupport@teamworks.com" : "support@teamworks.com";

  return loading ? (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Loading />
    </Box>
  ) : loginError ? (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="h1">{loginError.title}</Typography>
      <Typography variant="subtitle1">{loginError.subtext}</Typography>
    </Box>
  ) : showNoRolesError ? (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="h1">Your access hasn't been unlocked.</Typography>
      <Typography variant="subtitle1">
        Looks like Nutrition isn't unlocked for you yet. Once our staff assigns you a role, you'll be able to get started. Reach out to{" "}
        <a href={`mailto:${supportEmail}`}>{supportEmail}</a> with any questions.
      </Typography>
    </Box>
  ) : (
    (showJwtExpiredError || showGenericError) && (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="h1">{showJwtExpiredError ? "Expired Token" : "Error"}</Typography>
        <Typography variant="subtitle1">
          An error occurred, please try again or reach out to support at <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
        </Typography>
      </Box>
    )
  );
};
